
import { Poll } from '@/types/poll';
import { useAuth } from '@/contexts/auth';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * Hook to handle poll voting permissions with public voting support
 */
export const useVotePermissions = () => {
  const { user, profile } = useAuth();
  const { isVerifiedPollster, isAdmin, isSuperAdmin, hasCountryAccess } = usePermissions();
  
  // Check if user can vote on a specific poll
  const canVote = (poll?: Poll): boolean => {
    if (!user || !profile) {
      return false;
    }

    // Super admins can always vote
    if (isSuperAdmin()) {
      return true;
    }

    // Admins can always vote
    if (isAdmin()) {
      return true;
    }

    // Verified pollsters can always vote
    if (isVerifiedPollster()) {
      return true;
    }

    // Public users can vote only if public voting is allowed for the poll
    if (profile.role === 'public' && poll?.public_voting_allowed) {
      return true;
    }

    return false;
  };
  
  // Get reason why user can't vote
  const cannotVoteReason = (poll?: Poll): string | undefined => {
    if (!user) {
      return "You need to log in to participate in polls.";
    }

    if (!profile) {
      return "Profile information required to vote.";
    }

    // Super admins and admins can always vote
    if (isSuperAdmin() || isAdmin()) {
      return undefined;
    }

    // Verified pollsters can always vote
    if (isVerifiedPollster()) {
      return undefined;
    }

    // Public users need public voting to be enabled
    if (profile.role === 'public') {
      if (poll?.public_voting_allowed) {
        return undefined;
      } else {
        return "Public voting is not enabled for this poll.";
      }
    }

    // Unverified pollsters
    if (profile.role === 'pollster' && !profile.verified) {
      return "You need to be a verified pollster to participate in polls.";
    }
    
    return "You don't have permission to vote on this poll.";
  };
  
  // Check if pollster has access to this poll based on geography and country assignments
  const hasPollAccess = (poll: Poll): boolean => {
    // If not authenticated, no access
    if (!user || !profile) {
      return false;
    }

    // Super admins have access to all polls
    if (isSuperAdmin()) {
      return true;
    }

    // For public users, just check if public voting is allowed
    if (profile.role === 'public') {
      return !!poll.public_voting_allowed;
    }

    // For pollsters and admins, check country access
    if (poll.country_code && !hasCountryAccess(poll.country_code)) {
      return false;
    }
    
    // If poll is global (no country or constituency), allow access
    if (!poll.country_code && !poll.constituency_code) {
      return true;
    }
    
    // If poll has constituency and user doesn't match, deny access
    if (poll.constituency_code && poll.constituency_code !== profile.constituency_code) {
      return false;
    }
    
    // Passed all checks
    return true;
  };

  return {
    canVote,
    hasPollAccess,
    cannotVoteReason
  };
};
